import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel, SelectedFileStatus } from '@vizit/atoms';
import { FormDialog, FormDropZone } from '@vizit/forms';
import { PlusIcon } from '@vizit/icons';
import { useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import { FormProductCategoryComboBox, type FormProductCategoryComboBoxProps } from '../form-product-category-combo-box';
import { ImageTypeField } from '../image-type-field';
import { ProductTitleField } from '../product-title-field';

const ASSET_TYPE_SELECTION_OPTIONS = [
  { id: 'hero', name: 'Hero' },
  { id: 'carousel', name: 'Carousel' },
];

type ScoreAnImageFormSchema = {
  file: File;
  assetType: string;
  category: string;
  productTitle: string;
};

interface ScoreAnImageDialogProps {
  /**
   * Default values for the form
   */
  defaultValues?: ScoreAnImageFormSchema;
  /**
   * Query props for the product category combo box
   */
  productCategoryComboBoxProps: Omit<
    FormProductCategoryComboBoxProps<ScoreAnImageFormSchema>,
    'control' | 'name' | 'label' | 'isRequired'
  >;
  /**
   * Upload status from the form to display in the dialog
   */
  uploadStatus?: SelectedFileStatus;
  /**
   * Whether the dialog is open
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when the dialog is controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * The text for the trigger button
   *
   * @optional
   * @type {string}
   */
  triggerButtonText?: string;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * When true, the "Hero" image type is omitted from the image-type picker, leaving only "Carousel".
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  hideHeroOption?: boolean;
  /**
   * Callback function to handle the file status change
   * @param status - The status of the file
   */
  onFileStatusChange?: (status: SelectedFileStatus) => void;
  /**
   * Callback function to handle the score image api call
   */
  onSubmit: (data: ScoreAnImageFormSchema) => Promise<void>;
}

function ScoreAnImageDialog(props: ScoreAnImageDialogProps) {
  const {
    defaultValues = {
      file: undefined,
      assetType: '',
      category: '',
      productTitle: '',
    },
    uploadStatus,
    productCategoryComboBoxProps,
    isOpen,
    onOpenChange,
    hideTrigger = false,
    triggerButtonText = 'Score image',
    useIcon = false,
    hideHeroOption = false,
    onSubmit,
    onFileStatusChange,
  } = props;

  const assetTypeOptions = hideHeroOption
    ? ASSET_TYPE_SELECTION_OPTIONS.filter((option) => option.id !== 'hero')
    : ASSET_TYPE_SELECTION_OPTIONS;

  // Form and validation schema
  const formSchema = yup.object().shape({
    file: yup.mixed<File>().required('File is required'),
    assetType: yup
      .string()
      .oneOf(assetTypeOptions.map((option) => option.id))
      .required('Image type is required'),
    category: yup
      .string()
      .oneOf(
        productCategoryComboBoxProps.items.map((item) => item.id),
        'Product category is required',
      )
      .required('Product category is required'),
    productTitle: yup
      .string()
      .trim()
      .default('')
      .when('assetType', {
        is: 'hero',
        // biome-ignore lint/suspicious/noThenProperty: `then` is part of yup's conditional `.when()` API, not a thenable.
        then: (schema) => schema.required('Product title is required'),
        otherwise: (schema) => schema.optional(),
      }),
  });

  const methods = useForm<ScoreAnImageFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues,
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, setError, reset } = methods;

  const selectedAssetType = useWatch({ control, name: 'assetType' });

  const handleOnFileValidationError = (error?: string) => {
    if (error) {
      setError('file', { type: 'custom', message: error });
    }
  };

  const handleOnResetForm = () => {
    reset({
      file: undefined,
      assetType: '',
      category: '',
      productTitle: '',
    });

    // Reset the file status
    onFileStatusChange?.(SelectedFileStatus.Ready);
  };

  return (
    <FormDialog
      formName="score-an-image"
      formMethods={methods}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit(onSubmit)}
      onResetForm={handleOnResetForm}
      data-testid="score-image-dialog"
    >
      <FormDialog.Trigger>
        {hideTrigger ? (
          <Button className="sr-only" aria-hidden="true" isDisabled>
            Open dialog
          </Button>
        ) : (
          <Button level={ButtonLevel.Primary} aria-label={triggerButtonText} data-testid="score-image-trigger">
            {useIcon ? <PlusIcon size={20} /> : triggerButtonText}
          </Button>
        )}
      </FormDialog.Trigger>
      <FormDialog.Title>Upload an image</FormDialog.Title>
      <FormDialog.Subtitle>Upload a file and select a category and image type to get a score.</FormDialog.Subtitle>
      <FormDialog.FormFields>
        <div className="flex flex-col gap-10">
          <FormDropZone
            control={control}
            name="file"
            uploadStatus={uploadStatus}
            onFileValidationError={handleOnFileValidationError}
            onFileStatusChange={onFileStatusChange}
            dropZoneClassName="min-h-[118px]"
            selectedFilesClassName="[&:not(:empty)]:min-h-[118px]"
            data-testid="score-image-dropzone"
          />
          <ImageTypeField control={control} name="assetType" hideHeroOption={hideHeroOption} />
          <FormProductCategoryComboBox
            control={control}
            name="category"
            label="Product category"
            isRequired
            className="min-h-0"
            data-testid="score-image-category"
            {...productCategoryComboBoxProps}
          />
          {selectedAssetType === 'hero' && <ProductTitleField control={control} name="productTitle" />}
        </div>
      </FormDialog.FormFields>
      <FormDialog.SubmitButton data-testid="score-image-submit">Score my image!</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { ScoreAnImageDialog };
export type { ScoreAnImageFormSchema, ScoreAnImageDialogProps };
